"use client";

import React from "react";
import { ArrowRight, Loader, PlaneTakeoff } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICES = ["Ground Handling", "Landing Permit", "Crew Transport", "Catering", "Hotel Arrangement", "Fuel Supply", "Protocol Assistance", "Export/Import Clearance", "Aircraft Charter"];
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-xl border border-primary/20 bg-card/50 p-12 text-center backdrop-blur-sm">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/20 text-primary">
          <PlaneTakeoff className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="h-12 border-border/50 bg-background/50 text-foreground backdrop-blur-sm focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="h-12 border-border/50 bg-background/50 text-foreground backdrop-blur-sm focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground">Service Required</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-12 border-border/50 bg-background/50 text-foreground backdrop-blur-sm focus-visible:border-primary focus-visible:ring-primary/20">
                  <SelectValue placeholder="Select a service type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="border-border/50 bg-popover/95 backdrop-blur-md">
                {SERVICES.map((service, i) => <SelectItem key={service} value={service} data-index={i}>
                    {service}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group relative h-16 w-full overflow-hidden rounded-lg bg-gradient-to-r from-primary via-yellow-400 to-primary bg-[length:200%_auto] text-lg font-bold text-primary-foreground shadow-[0_0_40px_-10px_var(--color-primary)] transition-all hover:bg-[position:right_center] hover:shadow-[0_0_60px_-15px_var(--color-primary)]">
        <span className="relative z-10 flex items-center justify-center gap-2">
          {isSubmitting ? <>
              <Loader className="size-5 animate-spin" />
              Processing Request...
            </> : <>
              Request Ground Support
              <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
            </>}
        </span>
      </Button>
    </Form>;
}
export default function RequestGroundSupport() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="request-ground-support" className="dark relative overflow-hidden bg-background py-24 text-foreground lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0 bg-[repeating-linear-gradient(-45deg,transparent,transparent_40px,var(--color-foreground)_40px,var(--color-foreground)_41px)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </div>

      {}
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20 blur-[120px]">
        <div className="h-[400px] w-[600px] rounded-full bg-primary" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div ref={ref} className={`mx-auto max-w-3xl transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-extrabold tracking-tight text-foreground md:text-5xl lg:text-6xl">
              Ready for <span className="text-primary">Takeoff?</span>
            </h2>
            <p className="mx-auto max-w-xl text-lg text-muted-foreground">
              Partner with HLC for seamless aviation logistics and elite ground handling services at N’djili International Airport.
            </p>
          </div>

          <div className="rounded-2xl border border-border/50 bg-card/30 p-6 shadow-2xl backdrop-blur-md md:p-10">
            <FormProvider formId="request-ground-support-form" sectionName="Request Ground Support" defaultValues={{
            name: "",
            email: "",
            service: ""
          }} submitText="Request Ground Support" successMessage="Thank you for your request. Our operations team will contact you shortly to coordinate your ground support needs.">
              <SupportForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}